import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import cogoToast from "cogo-toast";
import AppContext from "../../AppContext";
import { SUCCESS_RESULT } from "../../Constants";
import { auth_me_api } from "../../services/Api";
import {
  applyAuthBootstrapData,
  getEncryptedUserData,
  getSafeRedirectUrl,
  setEncryptedAccessToken,
  setEncryptedRefreshToken,
  setTokenExpiresAt,
} from "../../services/Utils";

const AUTH_CALLBACK_IN_FLIGHT_KEY = "auth-callback-in-flight";

const readCallbackParams = () => {
  const params = new URLSearchParams(window.location.search);
  const expiresParam = params.get("expires_in");
  return {
    token: params.get("token"),
    refresh_token: params.get("refresh_token"),
    expires_in:
      expiresParam != null && expiresParam !== ""
        ? Number(expiresParam)
        : null,
    message: params.get("message") || params.get("error"),
  };
};

const AuthCallback = () => {
  const global = useContext(AppContext);
  const history = useHistory();
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const completeLogin = async () => {
      const { token, refresh_token, expires_in, message } = readCallbackParams();

      if (message && !token) {
        if (!cancelled) {
          setError(message);
        }
        return;
      }

      if (!token) {
        if (!cancelled) {
          setError("Missing login token. Please try signing in again.");
        }
        return;
      }

      if (sessionStorage.getItem(AUTH_CALLBACK_IN_FLIGHT_KEY) === "1") {
        return;
      }
      sessionStorage.setItem(AUTH_CALLBACK_IN_FLIGHT_KEY, "1");

      setEncryptedAccessToken(token);
      if (refresh_token) {
        setEncryptedRefreshToken(refresh_token);
      }
      if (expires_in != null && !Number.isNaN(expires_in)) {
        setTokenExpiresAt(expires_in);
      }

      try {
        const res = await auth_me_api();
        const response = res?.data;

        if (response?.status !== SUCCESS_RESULT || !response?.data?.user) {
          sessionStorage.removeItem(AUTH_CALLBACK_IN_FLIGHT_KEY);
          if (!cancelled) {
            setError(response?.message || "Failed to load user profile.");
          }
          return;
        }

        const saved = applyAuthBootstrapData(response.data, { token });
        if (!saved || !getEncryptedUserData()) {
          sessionStorage.removeItem(AUTH_CALLBACK_IN_FLIGHT_KEY);
          if (!cancelled) {
            setError("Failed to save login session. Please try again.");
          }
          return;
        }

        sessionStorage.removeItem(AUTH_CALLBACK_IN_FLIGHT_KEY);
        window.history.replaceState({}, document.title, "/auth/callback");

        global.setIs_loggedIn(true);
        cogoToast.success("Successfully signed in", { hideAfter: 3 });

        const redirectUrl = localStorage.getItem("redirectUrl");
        localStorage.removeItem("redirectUrl");
        const safeRedirectUrl = getSafeRedirectUrl(redirectUrl);

        window.setTimeout(() => {
          window.location.href = safeRedirectUrl;
        }, 100);
      } catch (err) {
        sessionStorage.removeItem(AUTH_CALLBACK_IN_FLIGHT_KEY);
        const apiMessage = err?.response?.data?.message;
        if (!cancelled) {
          setError(apiMessage || "Failed to complete sign in. Please try again.");
        }
      }
    };

    completeLogin();

    return () => {
      cancelled = true;
    };
    // Run once on mount; token stays in the URL until login succeeds (Strict Mode safe).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
      >
        <div style={{ maxWidth: "420px", textAlign: "center" }}>
          <h3>Sign in failed</h3>
          <p>{error}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => history.push("/login")}
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <p>Completing sign in...</p>
    </div>
  );
};

export default AuthCallback;
